import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchInput", 
    "selectAllCheckbox", 
    "leadCheckbox",
    "bulkActionsBar",
    "selectedCount",
    "leadRow"
  ]
  
  static values = {
    bulkApproveUrl: String,
    sendMessagesUrl: String
  }
  
  connect() {
    this.selectedLeads = new Set()
    this.updateBulkActionsBar()
  }
  
  toggleSelectAll(event) {
    const checked = event.target.checked
    
    this.leadCheckboxTargets.forEach(checkbox => {
      checkbox.checked = checked
      const leadId = checkbox.value
      
      if (checked) {
        this.selectedLeads.add(leadId)
      } else {
        this.selectedLeads.delete(leadId)
      }
    })
    
    this.updateBulkActionsBar()
  }
  
  toggleLead(event) {
    const leadId = event.target.value
    
    if (event.target.checked) {
      this.selectedLeads.add(leadId)
    } else {
      this.selectedLeads.delete(leadId)
    }
    
    this.updateBulkActionsBar()
    this.updateSelectAllCheckbox()
  }
  
  updateBulkActionsBar() {
    const count = this.selectedLeads.size
    
    if (count > 0) {
      this.bulkActionsBarTarget.classList.remove('hidden')
      if (this.hasSelectedCountTarget) {
        this.selectedCountTarget.textContent = count
      }
    } else {
      this.bulkActionsBarTarget.classList.add('hidden')
    }
  }
  
  updateSelectAllCheckbox() {
    if (!this.hasSelectAllCheckboxTarget) return
    
    const total = this.leadCheckboxTargets.length
    const selected = this.selectedLeads.size
    
    this.selectAllCheckboxTarget.checked = selected === total && total > 0
    this.selectAllCheckboxTarget.indeterminate = selected > 0 && selected < total
  }
  
  addToList(event) {
    event.preventDefault()
    
    // Open list selector modal
    const leadIds = Array.from(this.selectedLeads)
    
    // For now, show alert - would open modal in production
    if (confirm(`Add ${leadIds.length} leads to a list?`)) {
      this.showNotification(`${leadIds.length} leads added to list`, 'success')
    }
  }
  
  async bulkApprove(event) {
    event.preventDefault()
    
    const leadIds = Array.from(this.selectedLeads)
    
    if (!confirm(`Approve messages for ${leadIds.length} leads?`)) {
      return
    }
    
    // Show loading state
    const button = event.target
    const originalText = button.textContent
    button.disabled = true
    button.textContent = 'Approving...'
    
    try {
      const response = await fetch(this.bulkApproveUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ approval_ids: leadIds })
      })
      
      const data = await response.json()
      
      if (data.success) {
        this.showNotification(`${data.count} messages approved!`, 'success')
        this.refresh()
      } else {
        this.showNotification('Failed to approve messages', 'error')
      }
    } catch (error) {
      console.error('Error approving messages:', error)
      this.showNotification('Error approving messages', 'error')
    } finally {
      // Reset button state
      button.disabled = false
      button.textContent = originalText
    }
  }
  
  async sendMessages(event) {
    event.preventDefault()
    
    const leadIds = Array.from(this.selectedLeads)
    
    if (!confirm(`Send approved messages for ${leadIds.length} leads?`)) {
      return
    }
    
    // Show loading state
    const button = event.target
    const originalText = button.textContent
    button.disabled = true
    button.textContent = 'Sending...'
    
    try {
      const response = await fetch(this.sendMessagesUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ approval_ids: leadIds })
      })
      
      const data = await response.json()
      
      if (data.success) {
        const sent = data.results.success.length
        const failed = data.results.failed.length
        this.showNotification(`${sent} messages sent, ${failed} failed`, sent > 0 ? 'success' : 'error')
        this.refresh()
      } else {
        this.showNotification('Failed to send messages', 'error')
      }
    } catch (error) {
      console.error('Error sending messages:', error)
      this.showNotification('Error sending messages', 'error')
    } finally {
      // Reset button state
      button.disabled = false
      button.textContent = originalText
    }
  }
  
  async exportLeads(event) {
    event.preventDefault()
    
    const leadIds = Array.from(this.selectedLeads)
    
    if (leadIds.length === 0) {
      this.showNotification('Please select leads to export', 'error')
      return
    }
    
    const params = new URLSearchParams({ lead_ids: leadIds })
    window.location.href = `/leads/inbox/export?${params.toString()}`
  }
  
  async enrichEmails(event) {
    event.preventDefault()
    
    const leadIds = Array.from(this.selectedLeads)
    
    if (leadIds.length === 0) {
      this.showNotification('Please select leads to enrich', 'error')
      return
    }
    
    try {
      const response = await fetch('/leads/inbox/enrich_emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ lead_ids: leadIds })
      })
      
      const data = await response.json()
      
      if (data.success) {
        this.showNotification(data.message, 'success')
      }
    } catch (error) {
      this.showNotification('Error enriching emails', 'error')
    }
  }
  
  search(event) {
    const query = event.target.value
    
    // Debounced search
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }
  
  performSearch(query) {
    const url = new URL(window.location)
    if (query) {
      url.searchParams.set('search', query)
    } else {
      url.searchParams.delete('search')
    }
    Turbo.visit(url.toString())
  }
  
  filterCampaign(event) {
    const campaignId = event.target.value
    const url = new URL(window.location)
    
    if (campaignId) {
      url.searchParams.set('campaign_id', campaignId)
    } else {
      url.searchParams.delete('campaign_id')
    }
    
    Turbo.visit(url.toString())
  }
  
  rowClick(event) {
    // Don't navigate if clicking checkbox or other interactive elements
    if (event.target.tagName === 'INPUT' || event.target.closest('a') || event.target.closest('button')) {
      return
    }
    
    const row = event.currentTarget
    const leadId = row.dataset.leadId
    window.location.href = `/leads/inbox/${leadId}`
  }
  
  refresh() {
    Turbo.visit(window.location.toString(), { action: 'replace' })
  }
  
  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
  
  showNotification(message, type = 'info') {
    // Dispatch notification event for global handler
    const event = new CustomEvent('notification:show', {
      detail: { message, type }
    })
    window.dispatchEvent(event)
    
    // Fallback to console if no handler
    console.log(`[${type.toUpperCase()}] ${message}`)
  }
}

